import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Table from '../../core/pagination/datatable';
import Swal from 'sweetalert2';
import { ChevronUp, PlusCircle } from 'feather-icons-react/build/IconComponents';
import AddVariant from '../../core/modals/inventory/addvariant';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { setToogleHeader } from '../../core/redux/action';
import withReactContent from 'sweetalert2-react-content';
import { useToast } from '../../Toaster/ToastContext';
import Loader from '../loader/loader';
import { apiClient } from '../../services/apiClient';

const VariantAttributes = () => {
  
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [variants, setVariants] = useState([]);
  const [filteredVariants, setFilteredVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentVariant, setCurrentVariant] = useState(null);
  const { showToast } = useToast();
  const hasFetched = useRef(false);
  const [permissions, setPermissions] = useState({
    create: false,
    read: false,
    update: false,
    delete: false,
  });

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  )

  useEffect(() => {
    const storedPermissions = localStorage.getItem("rolespermissions");
    let parsedPermissions = [];
    try {
      const Permissions = JSON.parse(storedPermissions);
      const getPermissions = JSON.parse(Permissions.permissions)
      if(getPermissions.permissions){
        parsedPermissions = getPermissions.permissions;
      }
      else {
        parsedPermissions = getPermissions;
      }
        
      const inventoryPermissions = parsedPermissions.find(
        (item) => item.title === "Inventory"
      );

      const productPermissions = inventoryPermissions?.childPage?.find(
        (child) => child.title === "Variant Attributes"
      );
      // console.log("search product", productPermissions)

      if (productPermissions) {
        setPermissions({
          create: productPermissions.create || false,
          read: productPermissions.read || false,
          update: productPermissions.update || false,
          delete: productPermissions.delete || false,
        });
      } else {
        console.error("Product permissions not found in localStorage");
        showToast("Product permissions not found.", "error", "Permissions Error");
      }
    } catch (error) {
      console.error("Error parsing permissions from localStorage:", error);
      showToast("Failed to load permissions.", "error", "Permissions Error");
    }
  }, []);

  const fetchVariants = async () => {
    if (hasFetched.current) return;
    setLoading(true);
    try {
      const response = await apiClient.post('/paginate-varient', {
        pageIndex: 0,
        pageSize: 50,
        previousPageIndex: 0,
      });
      const data = await response;
        if (data.success) {
        setVariants(data.data);
        setFilteredVariants(data.data); 
      } else {
        setVariants([]);
        setFilteredVariants([]);
      }
    } catch (error) {
      showToast(error.message || 'Failed to fetch categories', 'error', 'Categories');
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  };

  useEffect(() => {
    fetchVariants();
  }, []);

  const handleAddVariants = () => {
    setCurrentVariant(null);
  };

  const handleEditVariants = (variant) => {
    setCurrentVariant(variant);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setFilteredVariants(
      variants.filter((variant) =>
        variant.name.toLowerCase().includes(term) ||
        variant.value.toLowerCase().includes(term) ||
        variant.status.toLowerCase().includes(term)
      )
    );
  };

  const columns = [
    {
      title: "Variant",
      dataIndex: "name",
      sorter: (a, b) => a.name.length - b.name.length,
    },

    {
      title: "Values",
      dataIndex: "value",
      sorter: (a, b) => {
        const aValue = Array.isArray(a.value) ? a.value : [];
        const bValue = Array.isArray(b.value) ? b.value : [];
        return aValue.length - bValue.length;
      },
      render: (value) => {
        if (Array.isArray(value)) {
          return value.join(', ');
        } else if (typeof value === 'string') {
          try {
            const parsedValue = JSON.parse(value.replace(/'/g, '"'));
            return Array.isArray(parsedValue) ? parsedValue.join(', ') : value;
          } catch (e) {
            return value;
          }
        }
        return ''; 
      },
    },

    {
      title: "Created On",
      dataIndex: "created_at",
      sorter: (a, b) => a.created_at.length - b.created_at.length,
      render: (text) => {
        const date = new Date(text);
        return isNaN(date) ? 'N/A' : date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).split('/').join('-');
      },
    },

    {
      title: "Status",
      dataIndex: "status",
      render: (text) => (
        <span className={`badge ${text === 'Active' ? 'badge-linesuccess' : 'badge-linedanger'}`}>
          {text === 'Active' ? 'Active' : 'Inactive'}
        </span>
      ),
      sorter: (a, b) => a.status.length - b.status.length,
    },

    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (text, record) => (
        <td className="action-table-data">
          <div className="edit-delete-action">
            {permissions.read && (
              <Link className="me-2 p-2" to="#" data-bs-toggle="modal" data-bs-target="#add-units"  onClick={() => handleEditVariants(record)}>
                <i data-feather="edit" className="feather-edit"></i>
              </Link>
            )}
            {permissions.delete && (
              <Link className="confirm-text p-2" to="#" onClick={() => showConfirmationAlert(record)} >
                <i data-feather="trash-2" className="feather-trash-2"></i>
              </Link>
            )}
          </div>
        </td>
      )
    },
  ]

  const MySwal = withReactContent(Swal);

  const showConfirmationAlert = async (record) => {
    try {
      const result = await MySwal.fire({
        title: 'Are you sure?',
        text: 'You won\'t be able to revert this!',
        showCancelButton: true,
        confirmButtonColor: '#00ff00',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonColor: '#ff0000',
        cancelButtonText: 'Cancel',
      });

      if (result.isConfirmed) {
        try {
          setLoading(true);
          await apiClient.post('/delete-varient', {
            id: record.id,
          });
          showToast('Variant deleted successfully', 'success', 'Variants');
          hasFetched.current = false;
          await fetchVariants();
        } catch (error) {
          showToast(error.message || 'Failed to delete variant', 'error', 'Variants');
        } finally {
          setLoading(false);
        }
      }
    } catch (error) {
      showToast('An error occurred', 'error', 'Variants');
    }
  };

  return (
    <>
    <Loader loading={loading} />
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Variant Attributes</h4>
              <h6>Manage your variant attributes</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={() => { dispatch(setToogleHeader(!data)) }}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
          <div className="page-btn">
            {permissions.create && (
              <a
                to="#"
                className="btn btn-added"
                data-bs-toggle="modal"
                data-bs-target="#add-units"
                onClick={handleAddVariants}
              >
                <PlusCircle className="me-2" />
                Add New Variant
              </a>
            )}
          </div>
        </div>
        {/* /product list */}
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm formsearch"
                    onChange={handleSearch}
                  />
                  <Link to className="btn btn-searchset">
                    <i data-feather="search" className="feather-search" />
                  </Link>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={filteredVariants}
                rowKey={(record) => record.id}
                rowSelection={null}
              />
            </div>
          </div>
        </div>
        {/* /product list */}
      </div>
      <AddVariant
        variant={currentVariant}
        onSave={() => {
          hasFetched.current = false;
          fetchVariants();
        }}
      />
    </div>
  </>
  )
}

export default VariantAttributes
